import { useState, useEffect, useCallback } from "react";
import {
  Lock,
  LogOut,
  Eye,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  ChevronDown,
  Shield,
  ArrowLeft,
  AlertTriangle,
  Timer,
  Download,
  Calendar,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "In attesa", labelEn: "Pending", color: "bg-amber-100 text-amber-800", icon: Clock },
  in_progress: { label: "In lavorazione", labelEn: "In Progress", color: "bg-blue-100 text-blue-800", icon: Loader2 },
  completed: { label: "Completata", labelEn: "Completed", color: "bg-green-100 text-green-800", icon: CheckCircle },
  rejected: { label: "Rifiutata", labelEn: "Rejected", color: "bg-red-100 text-red-800", icon: XCircle },
};

const typeConfig = {
  access: { label: "Accesso dati", labelEn: "Data Access", icon: Eye },
  deletion: { label: "Cancellazione", labelEn: "Deletion", icon: Trash2 },
};

function LoginPanel({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onLogin(res.data.token);
    } catch {
      setError("Password non valida");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center px-4">
      <div className="w-full max-w-sm" data-testid="admin-login-panel">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#0A0A0A] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-[#002FA7]" />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-[#111111]">Admin GDPR</h1>
          <p className="text-sm text-slate-500 mt-1">SI.SA. LINE S.R.L.</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#111111] mb-1.5">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Inserisci la password admin"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all"
                required
              />
            </div>
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>}
          <Button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full bg-[#002FA7] text-white hover:bg-[#001f75] rounded-full font-semibold"
          >
            {loading ? "Accesso..." : "Accedi"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="text-2xl font-black text-[#111111]">{value}</p>
    </div>
  );
}

function DeadlineProgress({ created, deadline }) {
  const now = new Date();
  const createdDate = new Date(created);
  const deadlineDate = new Date(deadline);
  const totalMs = deadlineDate - createdDate;
  const elapsedMs = now - createdDate;
  const progress = Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));
  const daysLeft = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));

  let barColor = "bg-green-500";
  if (daysLeft <= 0) barColor = "bg-red-600";
  else if (daysLeft <= 7) barColor = "bg-red-500";
  else if (daysLeft <= 15) barColor = "bg-amber-500";

  const deadlineFormatted = deadlineDate.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return (
    <div data-testid="deadline-progress" className="bg-slate-50 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 flex items-center gap-1">
          <Timer className="w-3 h-3" />
          Scadenza GDPR (30 giorni)
        </span>
        <span className={`text-xs font-semibold ${daysLeft <= 0 ? "text-red-600" : daysLeft <= 7 ? "text-red-500" : daysLeft <= 15 ? "text-amber-600" : "text-green-600"}`}>
          {daysLeft <= 0 ? `Scaduta da ${Math.abs(daysLeft)} giorni` : `${daysLeft} giorni rimasti`}
        </span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-2">
        <div
          data-testid="deadline-progress-bar"
          className={`h-2 rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-slate-400 mt-1.5">
        Scadenza: {deadlineFormatted}
      </p>
    </div>
  );
}

function DeadlineBadge({ deadline, status }) {
  if (status === "completed" || status === "rejected") return null;

  const now = new Date();
  const deadlineDate = new Date(deadline);
  const diffMs = deadlineDate - now;
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  let colorClass, text;
  if (daysLeft < 0) {
    colorClass = "bg-red-600 text-white animate-pulse";
    text = `Scaduta da ${Math.abs(daysLeft)}g`;
  } else if (daysLeft <= 7) {
    colorClass = "bg-red-100 text-red-700";
    text = `${daysLeft}g rimasti`;
  } else if (daysLeft <= 15) {
    colorClass = "bg-amber-100 text-amber-700";
    text = `${daysLeft}g rimasti`;
  } else {
    colorClass = "bg-green-100 text-green-700";
    text = `${daysLeft}g rimasti`;
  }

  return (
    <span
      data-testid="deadline-badge"
      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${colorClass}`}
    >
      <Timer className="w-3 h-3" />
      {text}
    </span>
  );
}

function RequestCard({ req, token, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(req.admin_notes || "");
  const [updating, setUpdating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const status = statusConfig[req.status] || statusConfig.pending;
  const type = typeConfig[req.request_type] || typeConfig.access;
  const StatusIcon = status.icon;
  const TypeIcon = type.icon;

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.patch(
        `${API}/gdpr-requests/${req.id}`,
        { status: newStatus, admin_notes: notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const deleteRequest = async () => {
    setUpdating(true);
    try {
      await axios.delete(
        `${API}/gdpr-requests/${req.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setUpdating(false);
      setConfirmDelete(false);
    }
  };

  const created = new Date(req.created_at);
  const formattedDate = created.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div
      data-testid="gdpr-request-card"
      className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-all"
    >
      <button
        data-testid="gdpr-request-toggle"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-all text-left"
      >
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
          req.request_type === "deletion" ? "bg-red-50" : "bg-blue-50"
        }`}>
          <TypeIcon className={`w-4 h-4 ${req.request_type === "deletion" ? "text-red-600" : "text-blue-600"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <p className="text-sm font-semibold text-[#111111] truncate">{req.full_name}</p>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${status.color}`}>
              {status.label}
            </span>
            {req.deadline && <DeadlineBadge deadline={req.deadline} status={req.status} />}
          </div>
          <p className="text-xs text-slate-500 truncate">{req.email} &middot; {type.label} &middot; {formattedDate}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-4">
          {/* Deadline progress bar */}
          {req.deadline && (req.status === "pending" || req.status === "in_progress") && (
            <DeadlineProgress created={req.created_at} deadline={req.deadline} />
          )}

          {req.message && (
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Messaggio</p>
              <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{req.message}</p>
            </div>
          )}

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Note Admin</p>
            <textarea
              data-testid="admin-notes-input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Aggiungi note..."
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] resize-none"
            />
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            {Object.entries(statusConfig).map(([key, conf]) => (
              <Button
                key={key}
                data-testid={`status-btn-${key}`}
                variant={req.status === key ? "default" : "outline"}
                size="sm"
                disabled={updating || req.status === key}
                onClick={() => updateStatus(key)}
                className={`rounded-full text-xs ${
                  req.status === key ? "bg-[#002FA7] text-white" : ""
                }`}
              >
                {conf.label}
              </Button>
            ))}

            <div className="ml-auto">
              {!confirmDelete ? (
                <Button
                  data-testid="delete-request-button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-full text-xs text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Elimina
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="confirm-delete-button"
                    size="sm"
                    disabled={updating}
                    onClick={deleteRequest}
                    className="rounded-full text-xs bg-red-600 text-white hover:bg-red-700"
                  >
                    {updating ? "..." : "Conferma"}
                  </Button>
                  <Button
                    data-testid="cancel-delete-button"
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmDelete(false)}
                    className="rounded-full text-xs"
                  >
                    Annulla
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportPanel({ token, lang = "it" }) {
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split("T")[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split("T")[0]);
  const [reportLang, setReportLang] = useState("it");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const handleGenerate = async () => {
    setError("");
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/gdpr-requests/report`,
        { date_from: dateFrom, date_to: dateTo, lang: reportLang },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `GDPR_Report_${dateFrom}_${dateTo}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(reportLang === "it" ? "Errore nella generazione del report" : "Error generating report");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="report-panel" className="bg-white rounded-xl border border-slate-200 p-5 mb-8">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-[#002FA7]" />
        <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-[#111111]">
          Report Compliance GDPR
        </h2>
      </div>
      <div className="flex flex-col sm:flex-row items-end gap-3">
        <div className="flex-1 w-full">
          <label className="block text-xs font-semibold text-slate-500 mb-1">
            <Calendar className="w-3 h-3 inline mr-1" />
            Da
          </label>
          <input
            data-testid="report-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7]"
          />
        </div>
        <div className="flex-1 w-full">
          <label className="block text-xs font-semibold text-slate-500 mb-1">
            <Calendar className="w-3 h-3 inline mr-1" />
            A
          </label>
          <input
            data-testid="report-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7]"
          />
        </div>
        <div className="w-full sm:w-auto">
          <label className="block text-xs font-semibold text-slate-500 mb-1">Lingua</label>
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button
              data-testid="report-lang-it"
              onClick={() => setReportLang("it")}
              className={`px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                reportLang === "it" ? "bg-[#002FA7] text-white" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              IT
            </button>
            <button
              data-testid="report-lang-en"
              onClick={() => setReportLang("en")}
              className={`px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                reportLang === "en" ? "bg-[#002FA7] text-white" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              EN
            </button>
          </div>
        </div>
        <Button
          data-testid="report-generate-button"
          onClick={handleGenerate}
          disabled={generating}
          className="bg-[#002FA7] text-white hover:bg-[#001f75] rounded-full px-6 w-full sm:w-auto"
        >
          {generating ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Generazione...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Genera PDF
            </span>
          )}
        </Button>
      </div>
      {error && <p data-testid="report-error" className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const verifyToken = useCallback(async () => {
    if (!token) {
      setChecking(false);
      return;
    }
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAuthenticated(true);
    } catch {
      sessionStorage.removeItem("admin_token");
      setToken("");
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const fetchData = useCallback(async () => {
    if (!token || !authenticated) return;
    try {
      const [reqRes, statsRes] = await Promise.all([
        axios.get(`${API}/gdpr-requests`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/gdpr-requests/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setRequests(reqRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogin = (newToken) => {
    sessionStorage.setItem("admin_token", newToken);
    setToken(newToken);
    setAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setToken("");
    setAuthenticated(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  if (!authenticated) {
    return <LoginPanel onLogin={handleLogin} />;
  }

  const getFilteredRequests = () => {
    let list = requests;
    if (filter === "expiring") {
      const now = new Date();
      list = requests.filter((r) => {
        if (r.status === "completed" || r.status === "rejected") return false;
        const deadline = new Date(r.deadline);
        const daysLeft = (deadline - now) / (1000 * 60 * 60 * 24);
        return daysLeft <= 7;
      });
    } else if (filter !== "all") {
      list = requests.filter((r) => r.status === filter);
    }
    // Sort: overdue first, then by days remaining, completed/rejected last
    return [...list].sort((a, b) => {
      const closedA = a.status === "completed" || a.status === "rejected";
      const closedB = b.status === "completed" || b.status === "rejected";
      if (closedA && !closedB) return 1;
      if (!closedA && closedB) return -1;
      if (closedA && closedB) return new Date(b.updated_at) - new Date(a.updated_at);
      const deadA = new Date(a.deadline || a.created_at);
      const deadB = new Date(b.deadline || b.created_at);
      return deadA - deadB;
    });
  };

  const filtered = getFilteredRequests();

  return (
    <div className="min-h-screen bg-[#F9F9F9]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0A0A0A] py-5">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-[#002FA7]" />
            <h1 className="text-lg font-bold text-white">GDPR Dashboard</h1>
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
              SI.SA. LINE S.R.L.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="admin-back-button"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-slate-400 hover:text-white rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Privacy Policy
            </Button>
            <Button
              data-testid="admin-logout-button"
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="bg-transparent border-white/20 text-white hover:bg-white hover:text-[#0A0A0A] rounded-full"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Report Panel */}
        <ReportPanel token={token} />

        {/* Stats */}
        {stats && (
          <div
            className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8"
            data-testid="admin-stats"
          >
            <StatCard label="Totale" value={stats.total} icon={FileText} color="text-slate-600" />
            <StatCard label="In attesa" value={stats.pending} icon={Clock} color="text-amber-600" />
            <StatCard label="In lavorazione" value={stats.in_progress} icon={Loader2} color="text-blue-600" />
            <StatCard label="Completate" value={stats.completed} icon={CheckCircle} color="text-green-600" />
            <StatCard label="Rifiutate" value={stats.rejected} icon={XCircle} color="text-red-600" />
            <StatCard label="In scadenza" value={stats.expiring_soon || 0} icon={AlertTriangle} color="text-amber-500" />
            <StatCard label="Scadute" value={stats.overdue || 0} icon={Timer} color="text-red-600" />
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="admin-filters">
          {[
            { key: "all", label: "Tutte" },
            { key: "pending", label: "In attesa" },
            { key: "in_progress", label: "In lavorazione" },
            { key: "completed", label: "Completate" },
            { key: "rejected", label: "Rifiutate" },
            { key: "expiring", label: "In scadenza" },
          ].map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                filter === f.key
                  ? "bg-[#002FA7] text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Request List */}
        <div className="space-y-3" data-testid="admin-request-list">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Nessuna richiesta trovata</p>
            </div>
          ) : (
            filtered.map((req) => (
              <RequestCard
                key={req.id}
                req={req}
                token={token}
                onUpdate={fetchData}
              />
            ))
          )}
        </div>
      </main>
    </div>
  );
}
